// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { StatusContext } from '@Context/StatusProvider';
import { THForm } from '@Molecules/THForm/THForm';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import useValidateForm, { UseValidateFormReturn } from '@Hooks/useValidateForm';
import THSelector from '@Molecules/THSelector/THSelector';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { ServerStatus } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';

import './CaseExportDrawer.scss';

interface CaseExportMispValues {
    servers: ServerStatus[];
}

interface CaseExportMispFormProps {
    mispServers: ServerStatus[];
    formStatus: UseValidateFormReturn;
    onFinish: (values: CaseExportMispValues) => void;
}

const CaseExportMispForm = ({ mispServers, formStatus, onFinish }: CaseExportMispFormProps) => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange } = formStatus;

    return (
        <THForm<CaseExportMispValues>
            form={form}
            onValuesChange={onFormValuesChange}
            editable
            className="case-export-form"
            onFinish={onFinish}
        >
            <THForm.Item
                name="servers"
                label="Servers"
                rules={[
                    {
                        validator: (_, value) => {
                            if ((value as ServerStatus[]).find(({ status }) => status === 'ERROR')) {
                                return Promise.reject('You cannot choose a server with the status ERROR');
                            }
                            if (!(value as ServerStatus[]).length) {
                                return Promise.reject('You must choose at least one server!');
                            }
                            return Promise.resolve();
                        },
                        required: true,
                    },
                ]}
            >
                <THSelector<ServerStatus> dataSource={mispServers} refKey="name">
                    {({ name, version, url, status }, selected) => (
                        <div className="export__selector__item">
                            {selected ? <ICONS.CheckCircleTwoTone /> : <ICONS.CheckCircleOutlined />}
                            <span className="export__selector__item__name">{`${name} (${version})`}</span>
                            <span className="export__selector__item__url">{url}</span>
                            <span className="export__selector__item__status">
                                <ColorLabel
                                    label={status === 'OK' ? i('general.available') : status}
                                    color={status === 'ERROR' ? 'red' : 'green'}
                                    className="export__selector__item__status-label"
                                />
                            </span>
                        </div>
                    )}
                </THSelector>
            </THForm.Item>

            <div className="case-export-form__button">
                <THButton type="primary" disabled={!isValid} htmlType="submit">
                    {i('misp.export.cta')}
                </THButton>
            </div>
        </THForm>
    );
};

interface CaseExportFormValues {
    password: string;
}

interface CaseExportArchiveFormProps {
    formStatus: UseValidateFormReturn;
    getExportUrl: (password?: string) => string;
}

const CaseExportArchiveForm = ({ formStatus, getExportUrl }: CaseExportArchiveFormProps) => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange } = formStatus;
    const [password, setPassword] = useState('');

    return (
        <THForm<CaseExportFormValues>
            name="case-export-form"
            form={form}
            onValuesChange={(_, values) => {
                setPassword(values.password);
                onFormValuesChange();
            }}
            editable
            className="case-export-form"
        >
            <THForm.Item name="password" label={i('case.export.file.password')} rules={[{ required: true }]}>
                <THPassword placeholder="Enter the archive password" />
            </THForm.Item>

            <div className="case-export-form__button">
                <THButton type="primary" disabled={!isValid} htmlType="button">
                    <ExternalLink url={getExportUrl(password)}>{i('case.export.file.cta')}</ExternalLink>
                </THButton>
            </div>
        </THForm>
    );
};

interface CaseExportDrawerProps {
    triggerElem: ReactElement;
    getExportUrl: (password?: string) => string;
    disabledExportFile?: boolean;
    disabledMisp?: boolean;
    onExportToMisp: (servers: ServerStatus[]) => void;
}

export const CaseExportDrawer = ({
    triggerElem,
    onExportToMisp,
    getExportUrl,
    disabledExportFile = true,
    disabledMisp = true,
}: CaseExportDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const { mispServers } = useContext(StatusContext);
    const archiveFormStatus = useValidateForm();
    const mispFormStatus = useValidateForm();

    const onMispFinish = ({ servers }: CaseExportMispValues) => {
        onExportToMisp(servers);
    };

    return (
        <THDrawer
            triggerElem={triggerElem}
            title={i('case.export.drawer-title')}
            afterVisibleChange={() => {
                archiveFormStatus.form.resetFields();
                mispFormStatus.form.resetFields();
            }}
        >
            {(toggleDrawer) => (
                <div className="export__list">
                    <div className="export__item">
                        <TitleSection>{i('case.export.file.title')}</TitleSection>

                        {!disabledExportFile ? (
                            <>
                                <p className="export__desc">{i('case.export.file.description')}</p>

                                <CaseExportArchiveForm formStatus={archiveFormStatus} getExportUrl={getExportUrl} />
                            </>
                        ) : (
                            <UnavailableMessage full type="capability" />
                        )}
                    </div>

                    <div className="export__item">
                        <TitleSection>{i('misp.title')}</TitleSection>

                        {!disabledMisp ? (
                            <>
                                <p className="export__desc">{i('misp.description')}</p>

                                <CaseExportMispForm
                                    mispServers={mispServers}
                                    formStatus={mispFormStatus}
                                    onFinish={(values) => {
                                        onMispFinish(values);
                                        toggleDrawer();
                                    }}
                                />
                            </>
                        ) : (
                            <UnavailableMessage full type="capability" />
                        )}
                    </div>
                </div>
            )}
        </THDrawer>
    );
};
